import { filter } from "rxjs/operators";
import React, { useLayoutEffect, useMemo } from "react";
import { GlobalHotKeys } from "react-hotkeys";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { Dimmer, Loader, Segment } from "semantic-ui-react";
import "react-animation/dist/keyframes.css";

import { useIsMobile } from "./utils";

import { currentPlaylist, currentTitle, viewingDescription } from "./atoms";
import { playingTrack, usePlaybackState } from "./Sequencer";
import { requestedTrack, snoozeModal, viewingVolumes } from "./atoms";

import { options$ } from "./OptionsStream";
import { commands$ } from "./CommandsStream";
import { tracks$, RATE, SELECT, TOGGLE } from "./TracksStream";
import { SAVE, SHUFFLE, UNDO, REDO, TOGGLE_VOLUMES } from "./CommandsStream";

import { shuffle } from "./shuffle";

import { Info } from "./Info";
import { Saver } from "./Saver";
import { Snooze } from "./Snooze";
import { Toolbar } from "./Toolbar";
import { Playlist } from "./Playlist";
import { Sequencer } from "./Sequencer";
import { PlayerTour } from "./PlayerTour";
import { FilterModal } from "./FilterModal";
import { OptionsStore } from "./OptionsStore";
import { ReorderModal } from "./ReorderModal";
import { AudioSequencer } from "./AudioSequencer";
import { DarkModeToggle } from "./DarkModeToggle";
import { LyricsSubtitle } from "./LyricsSubtitle";
import { DescriptionModal } from "./DescriptionModal";

import { MobileToolbar, mobileHeight } from "./MobileToolbar";
import { historyPush, historyRedo, historyUndo } from "./HistoryMachine";
import { MobileTransportModal } from "./MobileTransportModal";

const playerCommands$ = commands$.stream.pipe(
  filter(({ action }) =>
    [UNDO, REDO, SAVE, SHUFFLE, TOGGLE_VOLUMES].includes(action)
  )
);

const SHORTCUTS = {
  START_PAUSE: "Spacebar",
};

export function Player() {
  const title = useRecoilValue(currentTitle);
  const current = useRecoilValue(playingTrack);
  const requestTrack = useSetRecoilState(requestedTrack);
  const [playlist, setPlaylist] = useRecoilState(currentPlaylist);
  const [volumesOpen, setViewVolumes] = useRecoilState(viewingVolumes);

  const isMobileDevice = useIsMobile();

  const push = useSetRecoilState(historyPush);
  const redo = useSetRecoilState(historyRedo);
  const undo = useSetRecoilState(historyUndo);

  useLayoutEffect(() => {
    const subscription = playerCommands$.subscribe(({ action }) => {
      switch (action) {
        case SHUFFLE:
          push(playlist);

          const list = shuffle(playlist);

          setPlaylist(list);

          break;
        case UNDO:
          undo();
          break;
        case REDO:
          redo();
          break;
        case TOGGLE_VOLUMES:
          setViewVolumes(!volumesOpen);
          break;
        default:
          const data = playlist.map((track) => {
            const item = track.enabled ? {} : { enabled: false };

            return {
              url: track.url,
              title: track.title,
              rating: track.rating,
              authors: track.authors,
              volume: parseInt(track.volume),
              ...item,
            };
          });

          Saver.save({ title, playlist: data });
      }
    });

    return () => subscription.unsubscribe();
  }, [
    playlist,
    setPlaylist,
    title,
    volumesOpen,
    setViewVolumes,
    push,
    redo,
    undo,
  ]);

  useLayoutEffect(() => {
    const update = (track, updated) => {
      push(playlist);

      const list = playlist.map((e) => {
        return e.url === track.url ? updated : e;
      });

      setPlaylist(list);
    };

    const subscription = tracks$.stream.subscribe(({ action, track, data }) => {
      switch (action) {
        case SELECT:
          if (track.enabled) {
            if (track.url !== current.url && !track.error) {
              requestTrack(track.url);
            }

            Sequencer.select(track);
          }
          break;
        case TOGGLE:
          update(track, { ...track, enabled: !track.enabled });
          break;
        case RATE:
          const { rating } = data;
          update(track, { ...track, rating });
          break;
        default:
      }
    });

    return () => subscription.unsubscribe();
  }, [playlist, setPlaylist, current, requestTrack, push]);

  useLayoutEffect(() => {
    const subscription = options$.stream.subscribe((values) => {
      OptionsStore.save(values);
    });

    return () => subscription.unsubscribe();
  }, []);

  return isMobileDevice ? <MobileView /> : <DesktopView />
}

function DesktopView() {
  const [playlist, setPlaylist] = useRecoilState(currentPlaylist);
  const [snooze, toggleSnooze] = useRecoilState(snoozeModal);
  const push = useSetRecoilState(historyPush);

  const playbackState = usePlaybackState();

  const handlers = useMemo(() => {
    return playbackState === "idle" || playbackState === "paused"
      ? { START_PAUSE: () => commands$.play() }
      : { START_PAUSE: () => commands$.pause() };
  }, [playbackState]);

  return (
    <>
      <GlobalHotKeys keyMap={SHORTCUTS} handlers={handlers} allowChanges />

      <Toolbar>
        <Info />
      </Toolbar>

      <DarkModeToggle />

      <div>
        <List />
      </div>

      <LyricsSubtitle />
      <ReorderModal
        onSave={(list) => {
          push(playlist);

          setPlaylist(list);
        }}
      />
      <DescriptionModal />
      <PlayerTour />
      <FilterModal />
      {snooze ? <Snooze onStop={() => toggleSnooze(!snooze)} /> : null}
    </>
  );
}

function MobileView() {
  const [snooze, toggleSnooze] = useRecoilState(snoozeModal);

  usePlaybackState();

  const descriptionOpen = useRecoilValue(viewingDescription);

  const styles = {
    overflow: "auto",
    height: mobileHeight,
    display: descriptionOpen ? "none" : undefined
  }

  return <>
    <div style={styles}>
      <List />
    </div>
    <LyricsSubtitle />

    <FilterModal />
    <DescriptionModal />
    <MobileTransportModal />

    <MobileToolbar />

    {snooze ? <Snooze onStop={() => toggleSnooze(!snooze)} /> : null}
  </>
}

function List() {
  const [playlist, setPlaylist] = useRecoilState(currentPlaylist);
  const push = useSetRecoilState(historyPush);

  const isLoading = playlist.length === 0;

  return <Segment basic className="playlist">
    <Dimmer active={isLoading} page inverted>
      <Loader size="large">Loading</Loader>
    </Dimmer>

    <React.Suspense fallback={null}>
      <AudioSequencer />
    </React.Suspense>

    <Playlist
      onReorder={(list) => {
        push(playlist);

        setPlaylist(list);
      }}
    />
  </Segment>

}